import { store } from 'redux/store';
import {
  Council,
  CouncilAttendanceTypes,
  CouncilStatus,
  CouncilTypes,
  Society,
  User,
} from 'types';

import createDocumentFromHtml from 'utils/createDocumentFromHtml';

import duplicateCurrentTemplate from 'modules/boards/modals/ReplaceBoardTemplate/utils/duplicateCurrentTemplate';
import { AddCouncilFormState } from 'modules/_shared/components/Modals/AddCouncil/types/AddCouncilForm';
import { addCouncilDocuments } from 'modules/administratives/utils/addCouncilDocuments';

import { parseDirectorsToParticipants } from './parseDirectorsToParticipants';
import { generateCouncilTemplates } from './generateCouncilTemplates';
import { createCouncil } from '../redux/councilActions';
import { generateCouncilDocuments } from './generateCouncilDocuments';
import {
  councilActCertificateTemplates,
  councilActTemplates,
  councilAnnouncementTemplates,
} from '../constants/councilTemplates';
import { isActiveDirectorOnPast } from './isActiveDirector';
import { saveCouncilAnnouncement } from './saveCouncilAnnouncement';

interface NewCouncilData extends AddCouncilFormState {
  user: User;
  society: Society;
  announcementTemplate?: string;
  actTemplate?: string;
  actCertificateTemplate?: string;
}

const announcementTemplateId =
  councilAnnouncementTemplates[process.env.NODE_ENV];
const actTemplateId = councilActTemplates[process.env.NODE_ENV];
const certificationTemplateId =
  councilActCertificateTemplates[process.env.NODE_ENV];

export const saveCouncil = async (
  councilData: NewCouncilData,
  isDuplicate = false
) => {
  const {
    user,
    society,
    name,
    details,
    topics,
    minQuorum,
    files,
    date,
    councilType,
    attendanceType,
    place,
    announcementText,
    onlineAccess,
    digitalVoting,
    president,
    secretary,
    announcementTemplate: existingAnnouncementTemplate,
  } = councilData;

  try {
    const hasCouncilsV2Enabled = society?.configuration?.hasCouncilsV2Enabled;

    const uploadedDocuments = !isDuplicate
      ? await addCouncilDocuments({ files, user, society })
      : [];

    let announcementTemplate;

    if (isDuplicate) {
      announcementTemplate = await duplicateCurrentTemplate(
        existingAnnouncementTemplate as string
      );
    } else {
      const templates = await generateCouncilTemplates({ user, society });

      announcementTemplate = templates.announcementTemplate;
    }

    const activeDirectors = society?.directors.filter((director) =>
      isActiveDirectorOnPast(director, date)
    );

    const participants = parseDirectorsToParticipants(activeDirectors);
    const status = 'PENDING' as CouncilStatus;

    const newCouncil: Council = {
      name,
      status,
      date: date ? new Date(date) : new Date(),
      attendanceType: attendanceType as CouncilAttendanceTypes,
      place,
      details,
      topics,
      announcementText,
      onlineAccess,
      councilType: councilType as CouncilTypes,
      digitalVoting,
      participants,
      president,
      secretary,
      minQuorum,

      publicDocuments: uploadedDocuments,
      announcementTemplate: hasCouncilsV2Enabled
        ? announcementTemplateId
        : announcementTemplate?._id || null,
      actTemplate: actTemplateId,
      actCertificateTemplate: certificationTemplateId,
      society: society?.['_id'],
    };

    if (!hasCouncilsV2Enabled) {
      const announcementDocumentId = await saveCouncilAnnouncement({
        user,
        society,
        council: newCouncil,
        announcementTemplate,
      });

      if (announcementDocumentId) {
        newCouncil.announcementDocument = announcementDocumentId;
      }
    }

    const { announcementDocumentId, actDocumentId, certificationDocumentId } =
      await generateCouncilDocuments({
        announcementTemplateId: hasCouncilsV2Enabled
          ? announcementTemplateId
          : announcementTemplate?._id || null,
        actTemplateId,
        certificationTemplateId,
        society,
        council: newCouncil,
        userId: user['_id'],
        hasCouncilsV2Enabled,
      });

    if (actDocumentId) {
      newCouncil.actDocument = actDocumentId;
    }
    if (certificationDocumentId) {
      newCouncil.actCertificateDocument = certificationDocumentId;
    }
    if (announcementDocumentId && hasCouncilsV2Enabled) {
      newCouncil.announcementDocument = announcementDocumentId;
    }

    const council = await store.dispatch(createCouncil(newCouncil));

    return council;
  } catch (error) {
    throw new Error();
  }
};
